import Link from "next/link";
import axios from "axios";
import { motion } from "framer-motion";

import { Page, Header, Section, Button, Image, Breadcrumb, Reviews, Card, Markdown } from "@/components";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/ui/carousel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/ui/accordion";
import {
  tinaClient,
  useTina,
  tinaField,
  ANIMATED_ITEM,
  ANIMATED_CARD,
  ANIMATED_CONTAINER,
  fetchReviews,
} from "@/utils";

import { cn } from "@/lib/utils";

export default function About({ aboutQuery, reviews, globalData }) {
  const { data } = useTina({
    ...aboutQuery,
  });

  const { treatments } = globalData;
  const { header, opening, team, whyUs, sellingPoints, treatmentsCta, seo } = data.about;

  return (
    <Page
      className="[&>section]:mt-20 [&>section:first-of-type]:mt-12 lg:[&>section]:mt-32 lg:[&>section:first-of-type]:mt-24 pb-16 md:pb-24 lg:pb-32"
      seo={seo}
    >
      <Header header={header} className="bg-[50%_70%]" />
      <Opening opening={opening} />
      <Opening2 opening={opening} />
      <MainDoctor team={team} />
      <Team team={team} />
      <Reviews reviews={reviews} />
      <WhyUs whyUs={whyUs} />
      <SellingPoints sellingPoints={sellingPoints} />
      <TreatmentsCTA treatmentsCTA={treatmentsCta} treatments={treatments} />
    </Page>
  );
}

function Opening({ opening }) {
  const AnimatedButton = motion.create(Button);
  return (
    <Section
      wrapperClassName=""
      className="flex flex-col md:grid grid-cols-1 md:grid-cols-3 grid-rows-3 md:grid-rows-[2fr_1.5fr] gap-6"
      animate={true}
    >
      <div className="col-span-2">
        <motion.h2
          className="text-primary-400 h0 uppercase max-w-[630px]"
          data-tina-field={tinaField(opening, "title")}
          variants={ANIMATED_ITEM}
        >
          {opening.title}
        </motion.h2>
        <motion.p
          className="p-lg opacity-80 max-w-[470px] mt-6 mb-10"
          data-tina-field={tinaField(opening, "paragraph")}
          variants={ANIMATED_ITEM}
        >
          {opening.paragraph}
        </motion.p>
        <AnimatedButton variant="outline" data-tina-field={tinaField(opening.button, "label")} asChild>
          <Link href={opening?.button?.link} aria-label={opening?.button?.label}>
            {opening?.button?.label}
          </Link>
        </AnimatedButton>
      </div>
      <motion.div
        className="hidden md:block w-full h-full row-start-2 col-start-2"
        data-tina-field={tinaField(opening, "image")}
        variants={ANIMATED_ITEM}
      >
        <Image src={opening.image} alt={opening.title} className="w-full h-full rounded-lg" />
      </motion.div>
      <motion.div
        className="hidden md:flex w-full h-full row-start-1 row-span-2 col-start-3 items-end"
        data-tina-field={tinaField(opening, "imageTwo")}
        variants={ANIMATED_ITEM}
      >
        <Image src={opening.imageTwo} alt={opening.title} className="w-full h-full rounded-lg max-h-[700px]" />
      </motion.div>
    </Section>
  );
}

function Opening2({ opening }) {
  const AnimatedButton = motion.create(Button);
  return (
    <Section
      wrapperClassName=""
      className="flex flex-col md:grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-6"
      animate={true}
    >
      <motion.div
        className="hidden md:block w-full h-full"
        data-tina-field={tinaField(opening, "imageThree")}
        variants={ANIMATED_ITEM}
      >
        <Image src={opening.imageThree} alt={opening.titleTwo} className="w-full h-full rounded-lg max-w-[600px]" />
      </motion.div>
      <div className="text-right flex flex-col items-end">
        <motion.h2
          className="text-primary-400 h1 uppercase max-w-[630px]"
          data-tina-field={tinaField(opening, "titleTwo")}
          variants={ANIMATED_ITEM}
        >
          {opening.titleTwo}
        </motion.h2>
        <motion.p
          className="p-lg opacity-80 max-w-[470px] mt-6 mb-10"
          data-tina-field={tinaField(opening, "paragraphTwo")}
          variants={ANIMATED_ITEM}
        >
          {opening.paragraphTwo}
        </motion.p>
        <AnimatedButton
          key="button-two"
          variant="outline"
          data-tina-field={tinaField(opening.buttonTwo, "label")}
          asChild
        >
          <Link href={opening?.buttonTwo?.link} aria-label={opening?.buttonTwo?.label}>
            {opening?.buttonTwo?.label}
          </Link>
        </AnimatedButton>
      </div>
    </Section>
  );
}

function MainDoctor({ team }) {
  return (
    <Section wrapperClassName="lg:!mt-24" className="flex flex-col gap-12" id="team">
      <motion.hgroup
        className="flex flex-col gap-4"
        variants={ANIMATED_CONTAINER}
        initial="hide"
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
      >
        <motion.h2 className="h2" data-tina-field={tinaField(team, "title")} variants={ANIMATED_ITEM}>
          {team.title}
        </motion.h2>
        <motion.p className="p-lg" data-tina-field={tinaField(team, "subtitle")} variants={ANIMATED_ITEM}>
          {team.subtitle}
        </motion.p>
      </motion.hgroup>
      {team?.doctors?.map((doctor, index) => (
        <motion.div
          key={index}
          className="bg-secondary rounded-lg flex flex-col lg:flex-row overflow-hidden"
          variants={ANIMATED_ITEM}
          initial="hide"
          whileInView="show"
          viewport={{ once: true, amount: 0.4 }}
        >
          <div className="self-stretch flex-1 w-full" data-tina-field={tinaField(team.doctors[index], "image")}>
            <Image
              src={doctor?.image}
              alt={doctor?.name}
              className="w-full h-full min-h-[300px]"
              objectPosition="40%"
              width={417}
              height={523}
            />
          </div>
          <div className="flex-[1.5] p-8 lg:p-16">
            <Breadcrumb label={doctor?.role} data-tina-field={tinaField(team.doctors[index], "role")} />
            <h2 className="h2 mt-3 mb-4 lg:mb-6" data-tina-field={tinaField(team.doctors[index], "name")}>
              {doctor?.name}
            </h2>
            <p
              className={cn("p-lg opacity-80 mt-6 mb-10 line-clamp-[7]", index === 0 && "line-clamp-[8]")}
              data-tina-field={tinaField(team.doctors[index], "bio")}
            >
              {doctor?.bio}
            </p>
            <Dialog>
              <Button variant="secondaryLight" asChild>
                <DialogTrigger>Read More</DialogTrigger>
              </Button>
              <DialogContent className="w-full max-w-3xl max-h-[80svh] overflow-auto">
                <DialogHeader>
                  <DialogTitle className="h3 font-medium">{doctor?.name}</DialogTitle>
                  <DialogDescription
                    className="p font-light border-y border-black/30 !mt-4 pt-4 pb-4 !-mb-4"
                    data-tina-field={tinaField(team.doctors[index], "bio")}
                  >
                    {doctor?.bio}
                  </DialogDescription>
                </DialogHeader>
                <Accordion type="single" collapsible className="max-w-[720px]">
                  {doctor?.moreInfo?.sections?.map((section, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger>
                        <div data-tina-field={tinaField(team.doctors[index]?.moreInfo, `sections.${index}.title`)}>
                          {section.title}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent
                        className="markdown"
                        data-tina-field={tinaField(team.doctors[index]?.moreInfo, `sections.${index}.info`)}
                      >
                        <Markdown content={section?.info} />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </DialogContent>
            </Dialog>
          </div>
        </motion.div>
      ))}
    </Section>
  );
}

function Team({ team }) {
  if (!team?.members) return null;
  return (
    <Section wrapperClassName="lg:!mt-16" className="flex flex-col gap-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {team?.members?.map((member, index) => (
          <motion.div
            key={member.name}
            className="card overflow-hidden"
            initial="hide"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={ANIMATED_CARD((index % 3) * 0.1)}
          >
            <Image
              src={member.image}
              alt={member.name}
              className="w-full h-80"
              data-tina-field={tinaField(team, `members.${index}.image`)}
              width={404}
              height={320}
            />
            <div className="p-4">
              <p className="text-sm" data-tina-field={tinaField(team, `members.${index}.role`)}>
                {member.role}
              </p>
              <div className="h4 mt-2 mb-3" data-tina-field={tinaField(team, `members.${index}.name`)}>
                {member.name}
              </div>
              <p className="line-clamp-3" title={member.bio} data-tina-field={tinaField(team, `members.${index}.bio`)}>
                {member.bio}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function WhyUs({ whyUs }) {
  return (
    <Section>
      <motion.div
        className="bg-secondary flex flex-col-reverse lg:flex-row rounded-lg overflow-hidden"
        variants={ANIMATED_ITEM}
        initial="hide"
        whileInView="show"
        viewport={{ once: true, amount: 0.4 }}
      >
        <div className="flex-1 p-8 lg:py-14 lg:px-16">
          <Breadcrumb label={whyUs?.pretitle} data-tina-field={tinaField(whyUs, "pretitle")} />
          <h2 className="h2 mt-3 mb-4 lg:mb-6" data-tina-field={tinaField(whyUs, "title")}>
            {whyUs?.title}
          </h2>
          <div className="whitespace-pre-line" data-tina-field={tinaField(whyUs, "paragraph")}>
            {whyUs?.paragraph}
          </div>
          {whyUs?.button?.label && (
            <Button
              variant="secondaryLight"
              className="mt-8"
              data-tina-field={tinaField(whyUs, "button.label")}
              asChild
            >
              <Link href={whyUs?.button?.link} aria-label={whyUs?.button?.label}>
                {whyUs?.button?.label}
              </Link>
            </Button>
          )}
        </div>
        <div className="flex-1">
          <Image
            src={whyUs?.image}
            alt={whyUs?.title}
            className="w-full h-full min-h-[300px]"
            data-tina-field={tinaField(whyUs, "image")}
            width={574}
            height={471}
          />
        </div>
      </motion.div>
    </Section>
  );
}

function SellingPoints({ sellingPoints }) {
  return (
    <Section
      wrapperClassName="border-y border-x-0 border-2 border-dark "
      className="flex flex-col lg:flex-row lg:border-x border-dark divide-y lg:divide-y-0 lg:divide-x divide-dark"
      animate={true}
    >
      {sellingPoints?.points?.map((point, index) => (
        <motion.div className="flex-1 py-6 lg:px-8" key={index} variants={ANIMATED_ITEM}>
          <h3
            className="mb-4 lg:mb-16 font-semibold"
            data-tina-field={tinaField(sellingPoints, `points.${index}.title`)}
          >
            {point.title}
          </h3>
          <p data-tina-field={tinaField(sellingPoints, `points.${index}.paragraph`)}>{point.paragraph}</p>
        </motion.div>
      ))}
    </Section>
  );
}

function TreatmentsCTA({ treatmentsCTA, treatments }) {
  return (
    <Section wrapperClassName="overflow-hidden lg:-mb-12" className="space-y-6 md:space-y-12" animate={true}>
      <div className="flex flex-col md:flex-row justify-between gap-2">
        <div className="space-y-3">
          <motion.h2 className="h1" data-tina-field={tinaField(treatmentsCTA, "title")} variants={ANIMATED_ITEM}>
            {treatmentsCTA?.title}
          </motion.h2>
        </div>
        <motion.div className="md:mt-2" variants={ANIMATED_ITEM} data-tina-field={tinaField(treatmentsCTA, "button")}>
          <Button variant="outline" asChild className="hidden md:block">
            <Link href="/treatments" aria-label="View All Treatments">
              View All Treatments
            </Link>
          </Button>
          <Button
            variant="link"
            asChild
            className="block md:hidden"
            data-tina-field={tinaField(treatmentsCTA, "button")}
          >
            <Link href="/treatments" aria-label="View All Treatments">
              View All Treatments
            </Link>
          </Button>
        </motion.div>
      </div>
      <Carousel
        className="!overflow-visible [&>div]:!overflow-visible"
        opts={{
          align: "start",
          skipSnaps: true,
        }}
      >
        <CarouselContent className="!overflow-visible">
          {treatments?.treatmentList?.map(({ treatment }, index) => (
            <CarouselItem className="basis-[95%] sm:basis-[50%] lg:basis-[33%]" key={index}>
              <motion.div variants={ANIMATED_ITEM} className="h-full">
                <Card
                  className="h-full flex"
                  href={`/treatments/${treatment?._sys.filename}`}
                  title={treatment?.header?.title}
                  image={treatment?.header?.image}
                  imageSize={{ width: 410, height: 256 }}
                  description={treatment?.header?.subtitle}
                  button={{
                    label: "View Treatment",
                    link: `/treatments/${treatment?._sys.filename}`,
                  }}
                />
              </motion.div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="mt-4 lg:mt-8 relative flex gap-4">
          <CarouselPrevious className="!relative translate-y-0" />
          <CarouselNext className="!relative translate-y-0" />
        </div>
      </Carousel>
    </Section>
  );
}

export const getStaticProps = async () => {
  const aboutQuery = await tinaClient.queries.about({
    relativePath: "about.mdx",
  });

  const reviews = await fetchReviews();
  return {
    props: {
      aboutQuery,
      reviews,
    },
    revalidate: 60,
  };
};
